import React, { useCallback, useRef } from 'react'

import { ZLayers } from '~theme'
import { useOnEscape, useMinZIndex } from '~hooks'
import { Portal } from '~components/Portal'
import { Icon, IconName } from '~components/Icon'

import * as Styled from './Modal.styles'

export interface ModalProps {
  /**
   * Title
   */
  title?: string
  /**
   * Is modal visible
   */
  isVisible: boolean
  /**
   * Should modal be closed when pressing Esc
   */
  closeOnEsc?: boolean
  /**
   * Should modal be closed when clicked outside (on overlay)
   */
  closeOnClickOutside?: boolean
  /**
   * Callback to close the modal. If provided the 'x' would be rendered
   */
  onClose?: () => void
  /**
   * If `true`, shows a fixed 'X' on top right of screen.
   * If `false` or not provided, shows 'X' on the top right of _content_.
   *
   * If `onClose` isn't provided, this won't have any effect.
   */
  fixedCloseButton?: boolean
  /**
   * How much blur to apply to background, `undefined` or not passing means no blurring.
   */
  backgroundBlur?: Styled.BackgroundBlurValue
  /**
   * Whether the modal should show a window or not, defaults to true.
   */
  hasWindow?: boolean
  /** Custom root id */
  rootId?: string
  /** Custom root ref */
  rootRef?: React.RefObject<HTMLElement>
}

export type ComponentProps = ModalProps

const Modal: React.FC<ComponentProps> = ({
  title,
  isVisible,
  backgroundBlur,
  hasWindow = true,
  onClose,
  children,
  closeOnEsc = true,
  closeOnClickOutside = true,
  fixedCloseButton = false,
  rootId = 'modal-root',
  rootRef,
  ...otherProps
}) => {
  const overlayRef = useRef<HTMLDivElement>(null)
  const { zIndex, ZIndexContextProvider } = useMinZIndex(ZLayers.modal)

  const handleOverlayClick = useCallback(
    (e: React.MouseEvent) => {
      // We only want clicks on the overlay itself
      const targetIsOverlay = overlayRef.current && overlayRef.current === e.target

      if (onClose && closeOnClickOutside && targetIsOverlay) {
        onClose()
      }
    },
    [onClose, closeOnClickOutside],
  )

  const handleCloseOnEsc = useCallback(() => {
    onClose && onClose()
  }, [onClose])

  useOnEscape(handleCloseOnEsc, closeOnEsc && isVisible)

  return (
    <Portal rootId={rootId} rootRef={rootRef}>
      {isVisible && <Styled.BodyOverflowStyle />}
      <Styled.Overlay
        ref={overlayRef}
        onMouseDown={handleOverlayClick}
        isVisible={isVisible}
        backgroundBlur={backgroundBlur}
        zIndex={zIndex}
        {...otherProps}>
        <ZIndexContextProvider>
          <Styled.Window withSurface={hasWindow}>
            {title && (
              <Styled.Header>
                <Styled.Title>{title}</Styled.Title>
              </Styled.Header>
            )}
            {children}
            {onClose && (
              <Styled.CloseButton onMouseDown={onClose} fixedToScreen={fixedCloseButton}>
                <Icon name={IconName.times} size="large" />
              </Styled.CloseButton>
            )}
          </Styled.Window>
        </ZIndexContextProvider>
      </Styled.Overlay>
    </Portal>
  )
}

export default Modal
